import { useState } from "react";
import { Modal, IconButton, CircularProgress, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import { Document, Page, pdfjs } from "react-pdf";
import { getDocumentUrlByLedgerId } from "../Services/ApiCalling/banking";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export function usePdfPreview() {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [previewTitle, setPreviewTitle] = useState(
    "Terms & Conditions Preview",
  );
  const [hideHeader, setHideHeader] = useState(false);

  async function handlePreviewGeneralTerms(id, options = {}) {
    // options: { title?: string, hideHeader?: boolean }
    if (options.title) setPreviewTitle(options.title);
    setHideHeader(Boolean(options.hideHeader));

    if (typeof id === "string") {
      setPreviewUrl(id);
      setShowPreview(true);
    } else {
      setError("");
      setLoading(true);
      setNumPages(null);
      try {
        const response = await getDocumentUrlByLedgerId(id);
        const url = response.data;
        if (url) {
          setPreviewUrl(url);
          setShowPreview(true);
        } else {
          setError("Document URL not found");
        }
      } catch (err) {
        console.error("Error fetching document URL:", err);
        setError("Could not fetch document URL.");
      } finally {
        setLoading(false);
      }
    }
  }

  async function handleDownloadPdf() {
    if (!previewUrl) return;
    try {
      const response = await fetch(previewUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = previewTitle ? `${previewTitle}.pdf` : "document.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      const a = document.createElement("a");
      a.href = previewUrl;
      a.download = previewTitle ? `${previewTitle}.pdf` : "document.pdf";
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  }

  function PdfPreviewModal() {
    return (
      <Modal
        open={showPreview}
        onClose={() => setShowPreview(false)}
        BackdropProps={{
          style: { backgroundColor: "rgba(0,0,0,0.7)" },
        }}
        aria-labelledby="pdf-preview-title"
        aria-describedby="pdf-preview-description"
      >
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90vw",
            maxWidth: 900,
            height: "85vh",
            background: "#fff",
            borderRadius: 12,
            boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
            display: "flex",
            flexDirection: "column",
            outline: "none",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 20px",
              borderBottom: "1px solid #eee",
              background: "#f7f7f7",
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
            }}
          >
            {!hideHeader ? (
              <>
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: 18,
                    color: "var(--fourth-color-text)",
                  }}
                >
                  {previewTitle}
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <Tooltip title="Download PDF">
                    <span>
                      <IconButton
                        aria-label="download"
                        onClick={handleDownloadPdf}
                        size="small"
                        disabled={!previewUrl}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <IconButton
                    aria-label="close"
                    onClick={() => setShowPreview(false)}
                    size="small"
                  >
                    <CloseIcon />
                  </IconButton>
                </div>
              </>
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  marginLeft: "auto",
                }}
              >
                <Tooltip title="Download PDF">
                  <span>
                    <IconButton
                      aria-label="download"
                      onClick={handleDownloadPdf}
                      size="small"
                      disabled={!previewUrl}
                    >
                      <DownloadIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <IconButton
                  aria-label="close"
                  onClick={() => setShowPreview(false)}
                  size="small"
                >
                  <CloseIcon />
                </IconButton>
              </div>
            )}
          </div>
          <div
            style={{
              flex: 1,
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: loading || error ? "center" : "flex-start",
              padding: 16,
              background: "#fafbfc",
            }}
          >
            {loading && <CircularProgress />}
            {error && (
              <div style={{ color: "#d32f2f", fontWeight: 500 }}>{error}</div>
            )}
            {!loading && !error && previewUrl && (
              <Document
                file={previewUrl}
                onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                onLoadError={() => setError("Failed to load PDF.")}
                loading={<div>Loading PDF...</div>}
                error={<div>Failed to load PDF.</div>}
              >
                {Array.from(new Array(numPages), (el, index) => (
                  <Page
                    key={`page_${index + 1}`}
                    pageNumber={index + 1}
                    width={Math.min(window.innerWidth * 0.8, 800)}
                    style={{ marginBottom: 24 }}
                  />
                ))}
              </Document>
            )}
          </div>
        </div>
      </Modal>
    );
  }

  return { handlePreviewGeneralTerms, PdfPreviewModal };
}
